import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import {
    normalizePhone,
    validateCorporateEmail,
    validatePassword,
    validatePhone,
} from '../services/validation';
import { COUNTRIES } from '../config/countries';

interface RegisterScreenProps {
    onRegistered: (email: string) => void;
    onGoToLogin: () => void;
}

export default function RegisterScreen({ onRegistered, onGoToLogin }: RegisterScreenProps) {
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [country, setCountry] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setError(null);

        const emailError = validateCorporateEmail(email);
        if (emailError) {
            setError(emailError);
            return;
        }
        if (!company.trim()) {
            setError('Enter your company name.');
            return;
        }
        if (!country) {
            setError('Select your country.');
            return;
        }
        const phoneError = validatePhone(phone);
        if (phoneError) {
            setError(phoneError);
            return;
        }
        const passwordError = validatePassword(password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        setSubmitting(true);
        try {
            await getAuthService().signUp({
                email: email.trim(),
                password,
                company: company.trim(),
                country,
                phone: normalizePhone(phone) || undefined,
            });
            onRegistered(email.trim());
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Registration failed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthCard title="Create your account" subtitle="Register as a supplier">
            <Column spacing="400">
                <Input
                    id="reg-email"
                    type="email"
                    label="Email"
                    value={email}
                    autoComplete="email"
                    onChange={setEmail}
                />
                <Input
                    id="reg-company"
                    type="text"
                    label="Company name"
                    value={company}
                    autoComplete="organization"
                    onChange={setCompany}
                />
                <Select
                    id="reg-country"
                    label="Country"
                    placeholder="Select your country"
                    value={country}
                    onChange={setCountry}
                >
                    {COUNTRIES.map((c) => (
                        <SelectOption key={c.code} value={c.code} label={c.name} />
                    ))}
                </Select>
                <Input
                    id="reg-phone"
                    type="tel"
                    label="Phone number (optional)"
                    value={phone}
                    autoComplete="tel"
                    helperText="Include country code, e.g. +1 415 555 0123."
                    onChange={setPhone}
                />
                <Input
                    id="reg-password"
                    type="password"
                    label="Password"
                    value={password}
                    autoComplete="new-password"
                    helperText="At least 8 characters with upper, lower, and a number."
                    onChange={setPassword}
                />
                {error ? (
                    <Alert type="error" size="small">
                        {error}
                    </Alert>
                ) : null}
                <Button type="primary" disabled={submitting} onClick={handleSubmit}>
                    {submitting ? 'Creating account…' : 'Register'}
                </Button>
                <Button type="link" onClick={onGoToLogin}>
                    Already have an account? Sign in
                </Button>
            </Column>
        </AuthCard>
    );
}
